import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { getAdminApi } from '../services/api';

const CATEGORIES = [
  { value: 'features', label: 'Features' },
  { value: 'billing', label: 'Billing' },
  { value: 'troubleshooting', label: 'Troubleshooting' },
  { value: 'how-to', label: 'How-To' },
  { value: 'account', label: 'Account' },
  { value: 'other', label: 'Other' }
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
  { value: 'archived', label: 'Archived' }
];

const AdminKBEdit = () => {
  const { entryId } = useParams();
  const navigate = useAppNavigation();
  const routerNavigate = useNavigate();
  const isNew = entryId === 'new';
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Form state
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [category, setCategory] = useState('other');
  const [status, setStatus] = useState('active');
  const [priority, setPriority] = useState(0);
  const [tags, setTags] = useState('');
  const [metadata, setMetadata] = useState(null);

  useEffect(() => {
    if (!isNew) {
      fetchEntry();
    }
  }, [entryId]);

  const fetchEntry = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const adminApi = getAdminApi();
      const response = await adminApi.get(`/kb/${entryId}`);
      const entry = response.data.entry;
      
      setQuestion(entry.question || '');
      setAnswer(entry.answer || '');
      setCategory(entry.category || 'other');
      setStatus(entry.status || 'active');
      setPriority(entry.priority || 0);
      setTags(Array.isArray(entry.tags) ? entry.tags.join(', ') : '');
      setMetadata({
        created_by: entry.created_by,
        created_at: entry.created_at,
        updated_by: entry.updated_by,
        updated_at: entry.updated_at,
        times_used: entry.times_used,
        last_used_at: entry.last_used_at,
        source_ticket_id: entry.source_ticket_id
      });
    } catch (err) {
      console.error('Error fetching KB entry:', err);
      setError(err.response?.data?.error || 'Failed to load KB entry');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!question.trim() || !answer.trim()) {
      setError('Question and answer are required');
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      const adminApi = getAdminApi();
      const data = {
        question: question.trim(),
        answer: answer.trim(),
        category,
        status,
        priority: parseInt(priority) || 0,
        tags: tags.split(',').map(t => t.trim()).filter(t => t)
      };
      
      if (isNew) {
        await adminApi.post('/kb', data);
      } else {
        await adminApi.put(`/kb/${entryId}`, data);
      }
      
      setSuccess(true);
      setTimeout(() => {
        routerNavigate('/app/admin/kb');
      }, 1500);
    } catch (err) {
      console.error('Error saving KB entry:', err);
      setError(err.response?.data?.error || 'Failed to save KB entry');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" color="primary" elevation={0}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => routerNavigate('/app/admin/kb')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {isNew ? 'New KB Entry' : 'Edit KB Entry'}
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            KB entry saved successfully! Redirecting...
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Question & Answer
            </Typography>
            
            <TextField
              fullWidth
              label="Question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="What question does this entry answer?"
              multiline
              rows={2}
              sx={{ mb: 2 }}
              required
            />
            
            <TextField
              fullWidth
              label="Answer"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="Provide a clear, helpful answer..."
              multiline
              rows={6}
              required
            />
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Categorization
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={category}
                  label="Category"
                  onChange={(e) => setCategory(e.target.value)}
                >
                  {CATEGORIES.map(cat => (
                    <MenuItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  label="Status"
                  onChange={(e) => setStatus(e.target.value)}
                >
                  {STATUS_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField
                label="Priority"
                type="number"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                sx={{ width: 100 }}
                helperText="Higher = preferred"
              />
            </Box>
            
            <TextField
              fullWidth
              label="Tags"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="api, integration, feature-request"
              helperText="Comma-separated tags for filtering"
              sx={{ mt: 2 }}
            />
          </Paper>

          {metadata && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Metadata
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {metadata.source_ticket_id && (
                  <Chip 
                    label={`From Ticket #${metadata.source_ticket_id}`}
                    color="info"
                    size="small"
                  />
                )}
                <Chip 
                  label={`Used ${metadata.times_used || 0} times`}
                  size="small"
                />
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="caption" color="text.secondary" display="block">
                Created by: {metadata.created_by || 'Unknown'} on {metadata.created_at ? new Date(metadata.created_at).toLocaleString() : 'N/A'}
              </Typography>
              {metadata.updated_by && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Updated by: {metadata.updated_by} on {metadata.updated_at ? new Date(metadata.updated_at).toLocaleString() : 'N/A'}
                </Typography>
              )}
              {metadata.last_used_at && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Last used: {new Date(metadata.last_used_at).toLocaleString()}
                </Typography>
              )}
            </Paper>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => routerNavigate('/app/admin/kb')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={saving || !question.trim() || !answer.trim()}
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {saving ? 'Saving...' : 'Save Entry'}
            </Button>
          </Box>
        </form>
      </Container>
    </Box>
  );
};

export default AdminKBEdit;
